// Tidy-tree layout for the org chart.
//
// A top-down org chart needs each node centred over its children and no two
// subtrees overlapping. That is the classic Reingold–Tilford problem; this is
// the two-pass form of it, which is enough because these trees are small (a
// department plus five rungs) and never need the threading that the full
// algorithm uses for deep, uneven trees.
//
// Pass one measures each subtree's width bottom-up. Pass two walks top-down
// handing out a left edge, so siblings sit side by side and every parent lands
// on the midpoint of its first and last child.
//
// Pure functions on plain objects: the component keeps the layout out of render
// and only recomputes when the tree or the collapsed set changes.

export const NODE_W = 196;
export const NODE_H = 68;
export const H_GAP = 22;
export const V_GAP = 58;
export const DEPT_GAP = 72;

const childrenOf = (node, collapsed) =>
  collapsed.has(node.id) ? [] : node.children || [];

const measure = (node, collapsed, widths) => {
  const kids = childrenOf(node, collapsed);
  if (kids.length === 0) {
    widths.set(node.id, NODE_W);
    return NODE_W;
  }
  const spread =
    kids.reduce((sum, kid) => sum + measure(kid, collapsed, widths), 0) +
    H_GAP * (kids.length - 1);
  // A node is never narrower than its own box, or a single deep child would
  // let the parent's card overhang its slot.
  const width = Math.max(NODE_W, spread);
  widths.set(node.id, width);
  return width;
};

const place = (node, left, depth, collapsed, widths, out) => {
  const kids = childrenOf(node, collapsed);
  const y = depth * (NODE_H + V_GAP);

  if (kids.length === 0) {
    const x = left + widths.get(node.id) / 2;
    out.push({ node, depth, x, y });
    return { x, y };
  }

  const spread =
    kids.reduce((sum, kid) => sum + widths.get(kid.id), 0) +
    H_GAP * (kids.length - 1);
  // Centre the block of children inside this node's slot, which matters when
  // the node's own box is wider than its children need.
  let cursor = left + (widths.get(node.id) - spread) / 2;

  const placed = kids.map((kid) => {
    const result = place(kid, cursor, depth + 1, collapsed, widths, out);
    cursor += widths.get(kid.id) + H_GAP;
    return result;
  });

  const x = (placed[0].x + placed[placed.length - 1].x) / 2;
  out.push({ node, depth, x, y });
  return { x, y };
};

/**
 * Lays out one or more trees side by side.
 *
 * Returns flat lists rather than a nested structure: rendering wants to map over
 * boxes and over links independently, and edges need both endpoints resolved.
 */
export const layoutForest = (roots, collapsed = new Set()) => {
  const widths = new Map();
  const nodes = [];
  let cursor = 0;

  roots.forEach((root) => {
    measure(root, collapsed, widths);
    place(root, cursor, 0, collapsed, widths, nodes);
    cursor += widths.get(root.id) + DEPT_GAP;
  });

  const byId = new Map(nodes.map((entry) => [entry.node.id, entry]));

  // One link per parent→child pair that is actually on screen. Collapsed
  // subtrees contribute nothing, which is what makes collapsing cheap.
  const links = [];
  nodes.forEach(({ node }) => {
    childrenOf(node, collapsed).forEach((kid) => {
      const from = byId.get(node.id);
      const to = byId.get(kid.id);
      if (from && to) links.push({ from, to, parentId: node.id, childId: kid.id });
    });
  });

  const width = Math.max(cursor - DEPT_GAP, NODE_W);
  const height =
    nodes.reduce((max, entry) => Math.max(max, entry.y), 0) + NODE_H;

  return { nodes, links, byId, width, height };
};

/**
 * A smooth link between two boxes.
 *
 * Cubic rather than the usual right-angled elbow: the brief was explicitly to
 * get away from a boxy, default-admin look, and curves read as drawn rather
 * than as a table pretending to be a diagram.
 */
export const linkPath = (from, to) => {
  const x1 = from.x;
  const y1 = from.y + NODE_H;
  const x2 = to.x;
  const y2 = to.y;
  const mid = y1 + (y2 - y1) / 2;
  return `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`;
};
